import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { TeamRole, UserRole } from "../constants";
import {
  NotFoundPage,
  loggedInClientRoutes,
  loggedInCoachRoutes,
  loggedOutRoutes,
  publicRoutes,
  adminRoutes,
  workspaceOwnerRoutes,
} from "./groupedRoutes";
import { CurrentUserWrapper } from "../wrappers/current-user/CurrentUserWrapper";
import { useCurrentBrand } from "../hooks/useCurrentWorkspace";
import useAuth from "../hooks/auth/useAuth";
import ErrorBoundary from "../components/app/ErrorBoundary";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ThemeProvider } from "@mui/material";
import { createTheme } from "../theme";
import { JourneyRouteManager } from "./admin/onboardingTemplate/JourneyRouteManager";
import { JourneyRunContext } from "../contexts/JourneyRunContext";
import { useMediaMobile } from "../hooks/useMediaMobile";
import { DeepLinksNavigation } from "../components/app/DeepLinksNavigation";

interface AppRoutesProps {
  role: UserRole;
}
const AppRoutes = ({ role: userRole }: AppRoutesProps) => {
  const { isBrandedApp, isBrandedWorkspace } = useCurrentBrand();
  const user = useCurrentUser();
  const isClientArchived = user?.archived;

  const isCoach = userRole === UserRole.COACH;
  const isClient = userRole === UserRole.CLIENT;
  const isAdmin = user?.admin;
  const isOwner = user?.teamRole === TeamRole.OWNER;

  return (
    <Routes>
      {publicRoutes}
      {loggedOutRoutes(isBrandedApp, userRole)}
      {isCoach && loggedInCoachRoutes}
      {isClient && loggedInClientRoutes(isClientArchived)}
      {isAdmin && adminRoutes}
      {isOwner && workspaceOwnerRoutes}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const AppRouter = () => {
  const { userRole } = useAuth();
  const defaultTheme = createTheme();
  const isMobile = useMediaMobile("md", false);
  const [isRunning, setIsRunning] = React.useState(false);
  const [isForced, setIsForced] = React.useState(false);

  return (
    // Fallback to the default theme. Required to make styles works on components outside of the `CurrentUserWrapper`.
    // Will be overridden by workspace theme in `CurrentUserProvider` if exists.
    <ThemeProvider theme={defaultTheme}>
      <Router>
        <DeepLinksNavigation />
        <ErrorBoundary>
          <JourneyRunContext.Provider
            value={{ isRunning, setIsRunning, isForced, setIsForced }}
          >
            <Routes>
              <Route element={<CurrentUserWrapper authorized={!!userRole} />}>
                {userRole === UserRole.COACH && (
                  <Route
                    path="*"
                    element={
                      <>
                        {!isMobile && <JourneyRouteManager />}
                        <AppRoutes role={userRole} />
                      </>
                    }
                  />
                )}
                <Route path="/*" element={<AppRoutes role={userRole} />} />
              </Route>
            </Routes>
          </JourneyRunContext.Provider>
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
};

export default AppRouter;
